import React, { useId, useMemo, useRef, useState } from 'react';
import {
  Box, CircularProgress, IconButton, InputAdornment, Popper, TextField, ToggleButton, ToggleButtonGroup, Tooltip, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha } from '@mui/material/styles';
import { colors } from '../../styles/theme';
import { listSearchFieldSx, listSearchIconSx } from '../../Map/components/UserLotListsPanel/UserLotListsPanel.styles';
import { FLOATING_MODIFIERS, floatingPopperSx, leadRecordDropdownSx } from '../../Leads/components/LeadRecordSearch';
import { normalizeSearchText, parseSearchTerms } from '../../Map/utils/recordSearch';
import { getScrollEdgeFadeSx, useScrollEdgeFade } from '../../hooks/useScrollEdgeFade';
import { formatMetaMoney } from '../metaAdsMoney';
import { formatMetaContacts } from '../metaContacts';
import { metaCampaignLinkLabel } from '../metaCampaigns';
import { MetaActiveDot, isMetaDeliveryNoRecent, metaDeliveryShowsStatus } from './MetaDeliveryBits';
import { getMetaAdsStatusPillSx, metaViewToggleSx } from './metaAdsUi.styles';

/*
 * Buscador con desplegable de Campañas (reunión 29/10): reemplaza la lista de tarjetas. Mismo
 * patrón que `PlaceNameAutocomplete` (combobox + listbox en Popper, ↑ / ↓ + Enter, Escape cierra)
 * con el campo de búsqueda de Historial (`listSearchFieldSx`). Arriba del desplegable va el filtro
 * «Activas · Todas». Se elige una sola campaña; la ✕ la quita.
 */

const CAMPAIGN_TONES = { unlinked: 'pending', partial: 'partial', linked: 'done' };
const ID_QUERY = /^\d{4,}$/;

/**
 * ¿La campaña coincide con lo escrito? Por nombre (acentos, mayúsculas, varias palabras = todas,
 * como Historial) o por ID (4 dígitos o más, parte del ID).
 */
export function metaCampaignMatchesQuery(campaign, query) {
  const raw = String(query || '').trim();
  if (!raw) return true;
  const compact = raw.replace(/\s+/g, '');
  if (ID_QUERY.test(compact) && String(campaign.id || '').includes(compact)) return true;
  const terms = parseSearchTerms(raw);
  if (!terms.length) return true;
  const name = normalizeSearchText(campaign.name || '');
  return terms.every((t) => name.includes(t));
}

/** Segunda línea de la opción: objetivo · estado (si no está activa a secas) · gasto · contactos. */
export function metaCampaignOptionLine(campaign) {
  const { delivery } = campaign;
  let status = null;
  if (isMetaDeliveryNoRecent(delivery)) status = 'Sin mostrarse hoy ni ayer';
  else if (metaDeliveryShowsStatus(delivery)) status = delivery?.label || campaign.statusLabel || null;
  const spend = campaign.metrics?.spend != null ? formatMetaMoney(campaign.metrics.spend, campaign.currency) : null;
  const contacts = formatMetaContacts(campaign.metrics) || null;
  return [campaign.objectiveLabel, status, spend, contacts].filter(Boolean).join(' · ');
}

const popperListSx = {
  ...leadRecordDropdownSx,
  position: 'static',
  p: 0.75,
  display: 'flex',
  flexDirection: 'column',
};
const optionsSx = { display: 'flex', flexDirection: 'column', gap: 0.25, maxHeight: 300, overflowY: 'auto' };
const optionSx = {
  display: 'flex', alignItems: 'flex-start', gap: 1, px: 1, py: 0.75, borderRadius: '6px', cursor: 'pointer',
  '&:hover, &[data-active="true"]': { bgcolor: 'rgba(255,255,255,0.06)' },
  '&[aria-selected="true"]': { bgcolor: alpha(colors.primary, 0.14) },
};
const optionNameSx = { fontSize: '13px', lineHeight: '18px', color: 'rgba(255,255,255,0.85)', overflowWrap: 'anywhere' };
// Chip de vínculo de la opción (t993u): centrado con toda la opción (nombre + línea, aunque la
// línea ocupe 2 renglones); el punto de estado sigue con la primera línea.
const optionLinkPillSx = { alignSelf: 'center', flexShrink: 0, whiteSpace: 'nowrap' };
const optionLineSx = { fontSize: '11px', lineHeight: '15px', color: 'rgba(255,255,255,0.45)', overflowWrap: 'anywhere' };
const statusTextSx = { fontSize: '12px', lineHeight: '16px', color: 'rgba(255,255,255,0.55)', px: 1, py: 1 };
const adornmentButtonSx = {
  p: 0.25,
  color: 'rgba(255,255,255,0.6)',
  '&:hover': { color: colors.primaryLight, bgcolor: alpha(colors.primary, 0.12) },
  '&.Mui-disabled': { color: colors.disabledOnDarkText },
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * @param {{ campaigns: Array, selectedId: string|null, onSelect: (id: string|null) => void,
 *   includeInactive: boolean, onIncludeInactiveChange: (next: boolean) => void,
 *   activeLabel: string, allLabel: string, loading?: boolean, disabled?: boolean, placeholder?: string }} props
 */
export default function MetaCampaignPicker({
  campaigns,
  selectedId,
  onSelect,
  includeInactive,
  onIncludeInactiveChange,
  activeLabel,
  allLabel,
  // `loading`: la lista se está pidiendo (primera vez o recarga); el campo queda en gris con un loader.
  loading = false,
  disabled = false,
  placeholder = 'Buscá o elegí una campaña',
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const anchorRef = useRef(null);
  const inputRef = useRef(null);
  const listId = useId();
  const { scrollRef, fadeTop, fadeBottom } = useScrollEdgeFade();

  const selected = selectedId ? (campaigns || []).find((c) => String(c.id) === String(selectedId)) || null : null;
  const options = useMemo(
    () => (campaigns || []).filter((c) => metaCampaignMatchesQuery(c, query)),
    [campaigns, query],
  );
  // Cargando (primera carga o recarga): campo en gris con el loader a la derecha, sin abrir la lista.
  const locked = disabled || loading;
  const isOpen = open && !locked;
  const optionId = (index) => `${listId}-opt-${index}`;

  const openList = () => {
    if (locked) return;
    setOpen(true);
    setActiveIndex(-1);
  };
  const closeList = () => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
  };
  const pick = (campaign) => {
    onSelect(campaign.id);
    closeList();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape' && isOpen) {
      e.preventDefault();
      e.stopPropagation();
      closeList();
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      if (isOpen && activeIndex >= 0 && options[activeIndex]) pick(options[activeIndex]);
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!isOpen) {
        openList();
        return;
      }
      if (!options.length) return;
      const step = e.key === 'ArrowDown' ? 1 : -1;
      const next = (activeIndex + step + options.length) % options.length;
      setActiveIndex(next);
      document.getElementById(optionId(next))?.scrollIntoView?.({ block: 'nearest' });
    }
  };

  const trimmed = query.trim();
  let status = null;
  if (loading) status = 'Cargando campañas…';
  else if (!campaigns?.length) status = includeInactive ? 'No hay campañas' : 'No hay campañas activas';
  else if (!options.length) status = `Ninguna campaña coincide con «${trimmed}»`;
  let countText = '';
  if (!loading && campaigns?.length) {
    countText = trimmed ? `${options.length} de ${campaigns.length}` : plural(campaigns.length, 'campaña', 'campañas');
  }

  const anchorEl = () => anchorRef.current?.querySelector('.MuiOutlinedInput-root') || anchorRef.current;
  // Con la lista abierta el campo es para buscar; cerrada muestra la campaña elegida.
  const value = isOpen ? query : (selected?.name || '');

  return (
    <Box data-testid="meta-campaign-picker" sx={{ minWidth: 0 }}>
      <TextField
        ref={anchorRef}
        inputRef={inputRef}
        value={value}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); setActiveIndex(-1); }}
        onFocus={openList}
        onClick={() => { if (!isOpen) openList(); }}
        onBlur={closeList}
        onKeyDown={handleKeyDown}
        placeholder={loading && !selected ? 'Cargando campañas…' : (isOpen && selected ? selected.name : placeholder)}
        fullWidth
        size="small"
        variant="outlined"
        disabled={locked}
        inputProps={{
          maxLength: 80,
          'aria-label': 'Buscar campañas',
          role: 'combobox',
          'aria-autocomplete': 'list',
          'aria-expanded': isOpen,
          'aria-controls': isOpen ? listId : undefined,
          'aria-activedescendant': isOpen && activeIndex >= 0 ? optionId(activeIndex) : undefined,
          'aria-busy': loading || undefined,
        }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {selected && !isOpen ? (
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', width: 18, justifyContent: 'center' }}>
                  <MetaActiveDot delivery={selected.delivery} testId="meta-campaign-picker-dot" />
                </Box>
              ) : <SearchIcon sx={listSearchIconSx} />}
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end" sx={{ gap: 0.25, mr: -0.5 }}>
              {loading ? (
                <CircularProgress
                  size={16}
                  thickness={5}
                  sx={{ color: colors.primary, mx: 0.5 }}
                  aria-label="Cargando campañas"
                  data-testid="meta-campaign-picker-loading"
                />
              ) : null}
              {selected && !locked ? (
                <Tooltip title={<span>Quitar campaña</span>} placement="top" arrow>
                  <IconButton
                    size="small"
                    aria-label="Quitar campaña elegida"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { onSelect(null); closeList(); }}
                    sx={adornmentButtonSx}
                    data-testid="meta-campaign-picker-clear"
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
              {loading ? null : (
              <IconButton
                size="small"
                aria-label={isOpen ? 'Cerrar lista de campañas' : 'Abrir lista de campañas'}
                disabled={disabled}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (isOpen) {
                    closeList();
                    return;
                  }
                  inputRef.current?.focus();
                  openList();
                }}
                sx={adornmentButtonSx}
                data-testid="meta-campaign-picker-toggle"
              >
                <ExpandMoreIcon sx={{ fontSize: 20, transition: 'transform 150ms', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
              </IconButton>
              )}
            </InputAdornment>
          ),
        }}
        sx={{
          ...listSearchFieldSx,
          mt: 0,
          '& .MuiOutlinedInput-root': { ...listSearchFieldSx['& .MuiOutlinedInput-root'], pr: '8px' },
          '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: colors.disabledOnDarkText },
        }}
        data-testid="meta-campaigns-search"
      />
      <Popper
        open={isOpen}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={FLOATING_MODIFIERS}
        sx={floatingPopperSx}
        style={{ width: anchorEl()?.clientWidth || undefined }}
      >
        {/* mousedown sin default: el foco queda en el campo (el filtro no lo cierra). */}
        <Box onMouseDown={(e) => e.preventDefault()} sx={popperListSx} data-testid="meta-campaign-picker-list">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, px: 0.5, pb: 1, mb: 0.5, borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={includeInactive ? 'all' : 'active'}
              onChange={(_e, next) => { if (next) { onIncludeInactiveChange(next === 'all'); setActiveIndex(-1); } }}
              aria-label="Campañas a mostrar"
              data-testid="meta-campaigns-view-toggle"
              sx={metaViewToggleSx}
            >
              <ToggleButton value="active" data-testid="meta-campaigns-view-active">{activeLabel}</ToggleButton>
              <ToggleButton value="all" data-testid="meta-campaigns-view-all">{allLabel}</ToggleButton>
            </ToggleButtonGroup>
            {countText ? (
              <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', whiteSpace: 'nowrap' }} data-testid="meta-campaign-picker-count">{countText}</Typography>
            ) : null}
          </Box>
          {status ? <Typography role="status" sx={statusTextSx} data-testid="meta-campaign-picker-status">{status}</Typography> : null}
          {!loading && options.length ? (
            <Box
              id={listId}
              ref={scrollRef}
              role="listbox"
              aria-label="Campañas"
              sx={{ ...optionsSx, ...getScrollEdgeFadeSx(fadeTop, fadeBottom) }}
            >
              {options.map((c, index) => {
                const line = metaCampaignOptionLine(c);
                return (
                  <Box
                    key={c.id}
                    id={optionId(index)}
                    role="option"
                    aria-selected={String(c.id) === String(selectedId)}
                    data-active={index === activeIndex}
                    data-testid={`meta-campaign-option-${c.id}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => pick(c)}
                    sx={optionSx}
                  >
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', height: 18, flexShrink: 0 }}>
                      <MetaActiveDot delivery={c.delivery} testId={`meta-campaign-option-${c.id}-dot`} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={optionNameSx}>{c.name}</Typography>
                      {line ? <Typography sx={optionLineSx}>{line}</Typography> : null}
                    </Box>
                    <Box component="span" sx={{ ...getMetaAdsStatusPillSx(CAMPAIGN_TONES[c.linkStatus] || 'pending'), ...optionLinkPillSx }} data-testid={`meta-campaign-option-${c.id}-link`}>
                      {metaCampaignLinkLabel(c)}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ) : null}
        </Box>
      </Popper>
    </Box>
  );
}
